import { isNil, isEmpty, size, isFunction, uniq } from 'lodash';
import ServiceUrls from '@/lib/ServiceUrls';
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import { TimelineMax, TweenMax } from 'gsap';
import FilterMixin from './FilterMixin';
import HotelSettingsMixin from './HotelSettingsMixin';
import LocaleMixin from './LocaleMixin';
import UserConfigMixin from './UserConfigMixin';
import Redactyl from 'redactyl.js';

// define a mixin object
export default {
  props: ['lang', 'primaryLanguage'],

  mixins: [FilterMixin, HotelSettingsMixin, LocaleMixin, UserConfigMixin],

  data() {
    return {
      serviceUrls: ServiceUrls,
    };
  },

  updated: function () {
  },

  created: function () {

  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    ...mapGetters('hotel', ['loaded']),
  },

  methods: {
    randomString: function (n) {
      return Array(n)
        .fill(null)
        .map(() => (Math.random() < 0.5 ? ((Math.random() * 100) % 25) + 'A'.charCodeAt(0) : ((Math.random() * 100) % 25) + 'a'.charCodeAt(0)))
        .map(a => String.fromCharCode(a))
        .join('');
    },
    $copy: function (text) {
      const el = document.createElement('textarea');
      el.style.height = '0px';
      el.style.width = '0px';
      el.style.position = 'fixed';
      el.style.opacity = 0;
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    },
    glowEl: function ($el) {
      if (isNil($el)) return;
      $el.querySelectorAll('.hm-input > select,input,textarea,.v-select').forEach((el) => {
        const tl = new TimelineMax({
          onComplete: () => {
            el.style.boxShadow = null;
          },
        });
        tl.add(TweenMax.fromTo(el, 0.25, { boxShadow: '0 0 0 #2da295' }, { boxShadow: '0 0 0.5rem #2da295' }));
        tl.add(TweenMax.fromTo(el, 0.25, { boxShadow: '0 0 0.5rem #2da295' }, { boxShadow: '0 0 0 #2da295' })).delay(0.25);
      });
    },
    hasAnyLicense: function (licenseNames) {
      return licenseNames.some(it => this.hasLicense(it));
    },
    hasLicense: function (licenseName) {
      if (isNil(this.hotelSettings)) return false;
      return isNil(this.hotelSettings.details) ? false : this.hotelSettings.details.licenses.includes(licenseName);
    },
    isPartnerAdmin: function () {
      if (!this.hasRole('ROLE_SYSADMIN')) return false;

      const root = this.$root;
      if (!isNil(root)) {
        const user = root.user;
        if (!isNil(root.user)) {
          const userTenantId = user.tenantId;
          if (!isEmpty(userTenantId)) {
            if (!isEmpty(this.hotelSettings) && !isEmpty(this.hotelSettings.partnerAdmins)) {
              return this.hotelSettings.partnerAdmins.map(it => it.tenantId).includes(userTenantId);
            }
          }
        }
      }

      return false;
    },
    hasRole: function (roleName) {
      // if is sysadmin
      if (isNil(this.$root.user)) {
        return false;
      } else if (roleName !== 'ROLE_HOTELMEISTER_SUPER_ADMIN' && this.$root.user.roles.includes('ROLE_SYSADMIN')) {
        switch (roleName) {
          case 'ROLE_WEB_AGENCY':
          case 'ROLE_UNIT_OWNER':
          case 'ROLE_UNIT_OWNER_REPORTS':
            return false;
          default:
            return roleName.startsWith('ROLE_');
        }
      } else {
        return this.$root.user.roles.includes(roleName);
      }
    },
    hasAnyRole: function (roleNames) {
      if (isNil(this.$root.user)) return false;

      return roleNames.map(roleName => this.$root.user.roles.includes(roleName)).filter(it => it === true).length > 0;
    },
    showModal: function (content, cbConfirm, cbCancel = () => { }, title = null, doNotShowAgainMessage = false, config = {}) {
      this.showModalConfig({
        title: title,
        content: content,
        doNotShowAgainMessage: doNotShowAgainMessage,
        cbCancel: cbCancel,
        button1: {
          onClick: cbCancel,
        },
        button2: {
          onClick: cbConfirm,
        },
        ...config,
      });
    },
    show3ButtonModal: function (content, confirmLabel, additionalLabel, cbConfirm, cbAdditional, config = {}) {
      this.showModalConfig({
        content: content,
        button1: {
          color: 'green',
          onClick: cbAdditional,
          icon: 'save',
          label: additionalLabel,
        },
        button2: {
          onClick: cbConfirm,
          label: confirmLabel,
        },
        ...config,
      });
      // this.$root.modalMessageBus.$emit('show3ButtonModal', content, confirmLabel, additionalLabel, cbConfirm, cbAdditional, cbCancel, title, doNotShowAgainMessage);
    },
    showConfirmModal: function (content, action = () => { }, warning = null, config = {}) {
      this.showModalConfig({
        warning: warning,
        content: content,
        button2: {
          onClick: action,
          timer: config.timer || 5,
        },
        ...config,
      });
    },
    showModalConfig: function (config) {
      this.$root.modalMessageBus.$emit('showModalConfig', config);
    },
    findHotelSettingById: function (name, id, fieldName = 'id') {
      return this.hotelSettings[name].find(it => it[fieldName] === id);
    },
    checkEmpty: function (arr) {
      return isNil(arr) ? [] : arr;
    },
    isEmpty,
    isNil,
    intArray: function (value, offset = 0) {
      return [...Array(value).keys()].map(x => x + offset);
    },
    showMessages: function (response) {
      if (!isNil(response) && !isNil(response.data)) {
        let messageContainer = response;
        if (!isNil(response.warnings) || !isNil(response.errors) || !isNil(response.infos)) messageContainer = response;
        else if (!isNil(response.data) && (!isNil(response.data.warnings) || !isNil(response.data.errors) || !isNil(response.data.infos))) messageContainer = response.data;

        if (!isNil(messageContainer.infos) && size(messageContainer.infos) > 0) {
          messageContainer.infos.forEach((elem) => {
            this.$notify({
              group: 'msg',
              title: isNil(elem.message) ? this.$t('messages.info') : elem.title,
              text: isNil(elem.message) ? elem.title : elem.message,
              type: 'info',
              duration: 4500 * messageContainer.infos.length,
            });
            this.addUIBreadcrumb(
              'Displayed info message',
              {
                title: isNil(elem.message) ? this.$t('messages.error') : elem.title,
                text: isNil(elem.message) ? elem.title : elem.message,
              },
            );
            this.axios.post(this.$serviceUrls.log.warn, { title: elem.title, message: elem.message, date: new Date().toISOString() });
          });
        }
        if (!isNil(messageContainer.warnings) && size(messageContainer.warnings) > 0) {
          messageContainer.warnings.forEach((elem) => {
            this.$notify({
              group: 'msg',
              title: isNil(elem.message) ? this.$t('messages.warning') : elem.title,
              text: isNil(elem.message) ? elem.title : elem.message,
              type: 'warn',
              duration: 4500 * messageContainer.warnings.length,
            });
            this.addUIBreadcrumb(
              'Displayed warning message',
              {
                title: isNil(elem.message) ? this.$t('messages.error') : elem.title,
                text: isNil(elem.message) ? elem.title : elem.message,
              },
            );
            this.axios.post(this.$serviceUrls.log.warn, { title: elem.title, message: elem.message, date: new Date().toISOString() });
          });
        }
        if (!isNil(messageContainer.errors) && size(messageContainer.errors) > 0) {
          messageContainer.errors.forEach((elem) => {
            this.$notify({
              group: 'msg',
              title: isNil(elem.message) ? this.$t('messages.error') : elem.title,
              text: isNil(elem.message) ? elem.title : elem.message,
              type: 'error',
              duration: 6500 * messageContainer.errors.length,
            });
            try {
              this.addUIBreadcrumb(
                'Displayed error message',
                {
                  title: isNil(elem.message) ? this.$t('messages.error') : elem.title,
                  text: isNil(elem.message) ? elem.title : elem.message,
                },
                'warning',
              );
              // eslint-disable-next-line no-empty
            } catch (ignore) { }
            this.axios.post(this.$serviceUrls.log.error, { title: elem.title, message: elem.message, user: this.$root.username, url: window.location.href, hotel: this.$root.hotel, date: new Date().toISOString() });
          });
        // eslint-disable-next-line no-bitwise
        } else if (response.status >= 400) {
          this.$notify({
            group: 'msg',
            title: this.$t('messages.error'),
            text: null,
            type: 'error',
            duration: 5000,
          });
          try {
            this.addUIBreadcrumb(
              'Displayed error message',
              {
                text: this.$t('messages.error'),
              },
              'warning',
            );
            this.$rollbar.warn('Unexpected network error');
            this.axios.post(this.$serviceUrls.log.error, { msg: 'Unexpected network error', user: this.$root.username, url: window.location.href, hotel: this.$root.hotel, date: new Date().toISOString() });
            // eslint-disable-next-line no-empty
          } catch (ignore) { }
        }
      }
    },
    showErrorMessage: function (text, title = null) {
      this.$notify({
        group: 'msg',
        title: isNil(title) ? this.$t('messages.error') : title,
        text: text,
        type: 'error',
        duration: 5000,
      });
      try {
        this.addUIBreadcrumb(
          'Displayed error message',
          {
            text: text,
            title: title,
          },
          'warning',
        );
        this.axios.post(this.$serviceUrls.log.error, { text: text, title: title, user: this.$root.username, url: window.location.href, hotel: this.$root.hotel, date: new Date().toISOString() });
        // eslint-disable-next-line no-empty
      } catch (ignore) { }
    },
    showWarningMessage: function (text, title = null) {
      this.$notify({
        group: 'msg',
        title: isNil(title) ? this.$t('messages.warning') : title,
        text: text,
        type: 'warn',
        duration: 3000,
      });
      this.addUIBreadcrumb(
        'Displayed warning message',
        {
          title: title,
          text: text,
        },
      );
      this.axios.post(this.$serviceUrls.log.warn, { text: text, title: title, user: this.$root.username, url: window.location.href, hotel: this.$root.hotel, date: new Date().toISOString() });
    },
    reportError: function (action) {
      try {
        const redactly = new Redactyl({
          properties: [
            'pw',
            'pass',
            'passwd',
            'password',
            'password_confirmation',
            'passwordConfirmation',
            'confirm_password',
            'confirmPassword',
            'secret',
            'secret_token',
            'secretToken',
            'secret_key',
            'secretKey',
            'api_key',
            'access_token',
            'accessToken',
            'authenticity_token',
            'oauth_token',
            'token',
            'user_session_secret',
            'request.session.csrf',
            'request.session._csrf',
            'request.params._csrf',
            'request.cookie',
            'request.cookies'],
        });

        action = redactly.redact(action);

        this.$rollbar.captureEvent('manual', {
          text: action,
          title: action,
          user: this.$root.username,
          subdomain: this.hotelSettings.hotel.tenant.subdomain,
        }, 'warning');

        this.axios.post(this.$serviceUrls.log.error, { msg: action, user: this.$root.username, url: window.location.href, hotel: this.$root.hotel, date: new Date().toISOString() });
        // eslint-disable-next-line no-empty
      } catch (ignore) {
      }
    },
    addBreadcrumb: function (category, action, metadata = {}, severity = 'info') {
      try {
        this.$rollbar.captureEvent('manual', {
          category: category,
          action: action || '-',
          metadata: {
            ...metadata,
            url: window.location.href,
            hotel: this.$root.hotel,
            user: this.$root.username,
            subdomain: this.hotelSettings.hotel.tenant.subdomain,
          },
        }, severity);
        // eslint-disable-next-line no-empty
      } catch (ignore) { }
    },
    addUIBreadcrumb: function (action, metadata = {}, severity = 'info') {
      this.addBreadcrumb('UI', action, metadata, severity);
    },
    showSuccessMessage: function (text = null) {
      this.$notify({
        group: 'msg',
        title: this.$t('messages.success'),
        text: text,
        type: 'success',
        duration: 750,
      });
    },
    showOfflineMessage: function () {
      this.$notify({
        group: 'msg',
        title: this.$t('messages.offline.title'),
        text: this.$t('messages.offline.text'),
        type: 'error',
        duration: -1,
        data: {
          offline: true,
        },
      });
    },
    serviceUrl: function (name, params = []) {
      if (name.indexOf('.') >= 0) {
        const parts = name.split('.');

        let tmp = this.serviceUrls;

        parts.forEach((it) => { tmp = tmp[it]; });
        return tmp(this, ...params);
      } else {
        return this.serviceUrls[name](this, ...params);
      }
    },
    getI18nCountry: function (countryCode, locale = null) {
      if (isEmpty(locale)) locale = this.$shortLocale;
      if (isEmpty(countryCode)) return null;

      const countryName = this.$root.i18n.countries.getNames(locale)[countryCode];

      return countryName;
    },
    getI18nCountries: function (locale = null, additionalPriorities = []) {
      if (isEmpty(locale)) locale = this.$shortLocale;
      const priority = uniq([
        ...(additionalPriorities || []).filter(it => !isEmpty(it)),
        this.hotelSettings.details.countryCode,
        ...['AT', 'DE', 'CH', 'NL', 'BE', 'FR', 'IT', 'GB', 'CZ', 'SK', 'RU'],
      ]);
      return Object.entries(this.$root.i18n.countries.getNames(locale))
        .map(it => ({ id: it[0], name: it[1] }))
        .sort((a, b) => {
          if (priority.indexOf(a.id) >= 0 && priority.indexOf(b.id) < 0) {
            return -1;
          } else if (priority.indexOf(a.id) < 0 && priority.indexOf(b.id) >= 0) {
            return 1;
          } else if (priority.indexOf(a.id) >= 0 && priority.indexOf(b.id) >= 0) {
            return priority.indexOf(a.id) - priority.indexOf(b.id);
          } else {
            return a.name.localeCompare(b.name);
          }
        });
    },
    getI18nLanguages: function (locale = null) {
      if (isEmpty(locale)) locale = this.$shortLocale;
      const priority = ['de', 'nl', 'en', 'fr', 'it', 'ru', 'es', 'el', 'fr'];
      return Object.entries(this.$root.i18n.languages.getNames(locale))
        .map(it => ({ id: it[0], name: it[1] }))
        .sort((a, b) => {
          if (priority.indexOf(a.id) >= 0 && priority.indexOf(b.id) < 0) {
            return -1;
          } else if (priority.indexOf(a.id) < 0 && priority.indexOf(b.id) >= 0) {
            return 1;
          } else {
            return a.name.localeCompare(b.name);
          }
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/HotelmeisterMixin.js